import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { fireBurst } from "@/components/site/ConfettiLayer";
import { ACCENTS } from "@/data/site";

const REAL = 5.5;
const MAX = 10;
const fmt = (v) => v.toFixed(1).replace(".", ",");

export const ScreenQuiz = () => {
  const [guess, setGuess] = useState(3);
  const [revealed, setRevealed] = useState(false);

  const check = (e) => {
    setRevealed(true);
    if (Math.abs(guess - REAL) <= 0.5) fireBurst(e.clientX, e.clientY, ACCENTS.green);
  };

  const diff = guess - REAL;
  const message =
    Math.abs(diff) <= 0.5
      ? "Trafione! Masz świetne wyczucie."
      : diff < 0
      ? "To więcej, niż myślisz. Rzeczywistość to aż 5,5 godz. dziennie."
      : "Na szczęście trochę mniej — ale i tak aż 5,5 godz. dziennie.";

  return (
    <section id="quiz" data-testid="section-quiz" className="border-t border-hair py-12 md:py-20">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal>
          <span className="eyebrow" style={{ color: ACCENTS.blue }}>
            Sprawdź się
          </span>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 border border-hair lg:grid-cols-12">
          {/* question + slider */}
          <div className="border-b border-hair p-8 md:p-12 lg:col-span-6 lg:border-b-0 lg:border-r">
            <Reveal>
              <h2 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight text-ink md:text-4xl">
                Jak myślisz — ile godzin dziennie dzieci 8-12 lat spędzają przed ekranem?
              </h2>
            </Reveal>

            <div className="mt-10 flex items-baseline gap-3">
              <span className="font-heading text-6xl font-black leading-none tracking-tight text-ink md:text-7xl">
                {fmt(guess)}
              </span>
              <span className="text-sm font-bold uppercase tracking-[0.16em] text-graphite">godz.</span>
            </div>

            <input
              type="range"
              min={0}
              max={MAX}
              step={0.5}
              value={guess}
              onChange={(e) => {
                setGuess(Number(e.target.value));
                setRevealed(false);
              }}
              data-testid="quiz-slider"
              aria-label="Twój typ w godzinach"
              className="owo-range mt-8 w-full"
            />
            <div className="mt-3 flex justify-between text-xs font-bold text-graphite">
              <span>0 godz.</span>
              <span>{MAX} godz.</span>
            </div>

            <button
              data-testid="quiz-check"
              onClick={check}
              className="mt-8 bg-owo-pink px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-ink transition-colors hover:bg-[#ef7ba6]"
            >
              Sprawdź odpowiedź
            </button>
          </div>

          {/* reveal */}
          <div className="relative flex min-h-[320px] flex-col justify-center p-8 md:p-12 lg:col-span-6">
            <div className="owo-dots-bg pointer-events-none absolute inset-0 opacity-40" aria-hidden />
            <AnimatePresence mode="wait">
              {revealed ? (
                <motion.div
                  key="answer"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="relative"
                  data-testid="quiz-answer"
                >
                  <span className="eyebrow">Prawdziwa odpowiedź</span>
                  <div className="mt-4 flex items-baseline gap-3">
                    <span
                      className="font-heading text-7xl font-black leading-none tracking-tight md:text-8xl"
                      style={{ color: ACCENTS.pink }}
                    >
                      5,5
                    </span>
                    <span className="text-sm font-bold uppercase tracking-[0.16em] text-graphite">godz.</span>
                  </div>
                  <p className="mt-5 max-w-md text-base font-medium text-ink">{message}</p>

                  {/* comparison bars */}
                  <div className="mt-8 space-y-4">
                    {[
                      { label: "Twój typ", val: guess, color: ACCENTS.blue },
                      { label: "Rzeczywistość", val: REAL, color: ACCENTS.pink },
                    ].map((b) => (
                      <div key={b.label}>
                        <div className="flex justify-between text-xs font-bold uppercase tracking-[0.14em] text-graphite">
                          <span>{b.label}</span>
                          <span>{fmt(b.val)} godz.</span>
                        </div>
                        <div className="mt-2 h-3 w-full bg-hair">
                          <motion.div
                            className="h-full"
                            style={{ backgroundColor: b.color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${(b.val / MAX) * 100}%` }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 text-xs uppercase tracking-[0.14em] text-graphite">
                    Źródło: Common Sense Media, 2025
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="prompt"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative"
                >
                  <p className="font-heading text-2xl font-bold leading-tight text-ink/40 md:text-3xl">
                    Przesuń suwak, ustaw swój typ i sprawdź, jak blisko jesteś prawdy.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
